// ContentOsAgentsV3.tsx
// "Build While You Sleep" — AI Agents autônomos
// @guilhermeribeiro.ai | Linha: Máquina de Um | CTA: AGENTE
// v3 CHANGES:
//   - BgKineticLines (SC-001 hook), BgCircuitTrace (SC-002 pipeline),
//     BgDataFlow (SC-005 numbers), BgGradientConic (SC-007 CTA)
//   - CaptionBar: burned-in word-by-word captions (85% watch muted)
// VO-FIRST: 698 frames @ 30fps = 23.3s

import React from "react";
import {
  AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig,
  interpolate, spring, Audio, staticFile, Img,
} from "remotion";
import {CLAMP, FONT_STACK} from "../presets/idv";
import {SPRINGS} from "../presets/editing-styles";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {BgKineticLines} from "../components/backgrounds/BgKineticLines";
import {BgCircuitTrace} from "../components/backgrounds/BgCircuitTrace";
import {BgDataFlow} from "../components/backgrounds/BgDataFlow";
import {BgGradientConic} from "../components/backgrounds/BgGradientConic";

export const AGENTS_V3_TOTAL = 698;

// ─── Color system ─────────────────────────────────────────────────────────────
const C = {
  bg: "#080808", white: "#FFFFFF", pain: "#EF4444",
  amber: "#F59E0B", teal: "#14B8A6", muted: "rgba(255,255,255,0.45)",
} as const;

const PILL_TOP = 52;
const HERO_TOP = 320;

// ─── VO-FIRST scene timing (measured from ElevenLabs audio) ──────────────────
const SC = {
  s1: {from: 0,   dur: 75},   // ag01 2.5s  — HOOK
  s2: {from: 75,  dur: 143},  // ag02 4.8s  — TIMELINE PROOF
  s3: {from: 218, dur: 91},   // ag03 3.0s  — REVEAL
  s4: {from: 309, dur: 79},   // ag04 2.6s  — MECHANISM
  s5: {from: 388, dur: 158},  // ag05 5.3s  — NUMBERS
  s6: {from: 546, dur: 77},   // ag06 2.6s  — EPIPHANY
  s7: {from: 623, dur: 75},   // ag07 2.5s  — CTA AGENTE
} as const;

// ─── Caption word type ────────────────────────────────────────────────────────
type CaptionWord = {text: string; frame: number};

// ─── CaptionBar — burned-in word-by-word (accessibility + muted viewers) ─────
// Critical: 85% Instagram views are muted. Caption = +47% retention.
const CaptionBar: React.FC<{words: CaptionWord[]; bottom?: number}> = ({
  words,
  bottom = 120,
}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();

  const activeIndex = words.reduce((acc, w, i) => (f >= w.frame ? i : acc), -1);
  if (activeIndex < 0) return null;

  return (
    <div style={{
      position: "absolute", left: 36, right: 36, bottom,
      background: "rgba(0,0,0,0.68)",
      borderRadius: 12,
      padding: "14px 22px",
      display: "flex",
      flexWrap: "wrap",
      gap: "0 0.28em",
      justifyContent: "center",
      alignItems: "baseline",
      border: "1px solid rgba(255,255,255,0.06)",
    }}>
      {words.map((w, i) => {
        if (f < w.frame) return null;
        const isActive = i === activeIndex;
        const p = spring({frame: f - w.frame, fps, config: SPRINGS.snappy});
        return (
          <span key={i} style={{
            fontSize: 18,
            fontWeight: isActive ? 800 : 500,
            color: isActive ? C.amber : "rgba(255,255,255,0.8)",
            fontFamily: FONT_STACK,
            opacity: interpolate(p, [0, 1], [0, 1], CLAMP),
            transform: `scale(${interpolate(p, [0, 1], [0.9, 1], CLAMP)})`,
            willChange: "transform,opacity",
            display: "inline-block",
            lineHeight: 1.5,
          }}>{w.text}</span>
        );
      })}
    </div>
  );
};

// ─── BgNoiseSolid (kept inline for SC-006) ────────────────────────────────────
const BgNoiseSolid: React.FC<{tint?: string}> = ({tint = "transparent"}) => {
  const f = useCurrentFrame();
  const noise = Array.from({length: 12}, (_, i) => {
    const x = (i * 137.5) % 100;
    const y = (i * 97.3) % 100;
    const drift = Math.sin(f * 0.002 + i) * 1.5;
    return {x, y: y + drift, s: 1 + (i % 3), o: 0.03 + (i % 2) * 0.015};
  });
  return (
    <AbsoluteFill style={{background: C.bg}}>
      {tint !== "transparent" && (
        <div style={{position:"absolute",inset:0,
          background:`radial-gradient(ellipse at 50% 30%, ${tint}18 0%, transparent 65%)`}}/>
      )}
      {noise.map((p, i) => (
        <div key={i} style={{position:"absolute",left:`${p.x}%`,top:`${p.y}%`,
          width:p.s,height:p.s,background:"#fff",opacity:p.o,borderRadius:1,
          willChange:"top",pointerEvents:"none"}}/>
      ))}
      <div style={{position:"absolute",inset:0,
        background:"radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(0,0,0,0.8) 100%)",
        pointerEvents:"none"}}/>
    </AbsoluteFill>
  );
};

// ─── MeshBg (kept for SC-003, SC-004) ────────────────────────────────────────
const MeshBg: React.FC<{color: string; opacity?: number; speed?: number}> =
  ({color, opacity = 0.22, speed = 1}) => {
  const f = useCurrentFrame();
  const hex = Math.round(opacity * 255).toString(16).padStart(2,"0");
  const b1x = 30 + Math.sin(f * 0.008 * speed) * 8;
  const b1y = 25 + Math.cos(f * 0.006 * speed) * 6;
  const b2x = 70 + Math.cos(f * 0.007 * speed) * 10;
  const b2y = 60 + Math.sin(f * 0.005 * speed) * 8;
  return (
    <AbsoluteFill style={{background: C.bg, overflow:"hidden"}}>
      <div style={{position:"absolute",left:`${b1x}%`,top:`${b1y}%`,
        width:"70%",height:"70%",transform:"translate(-50%,-50%)",
        background:`radial-gradient(circle, ${color}${hex} 0%, transparent 70%)`,
        filter:"blur(60px)",willChange:"left,top"}}/>
      <div style={{position:"absolute",left:`${b2x}%`,top:`${b2y}%`,
        width:"55%",height:"55%",transform:"translate(-50%,-50%)",
        background:`radial-gradient(circle, ${color}33 0%, transparent 65%)`,
        filter:"blur(90px)",willChange:"left,top"}}/>
      <div style={{position:"absolute",inset:0,
        background:"radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.65) 100%)",
        pointerEvents:"none"}}/>
    </AbsoluteFill>
  );
};

// ─── Particles ────────────────────────────────────────────────────────────────
const Particles: React.FC = () => {
  const f = useCurrentFrame();
  return <>
    {[0,1,2,3,4,5].map(i => {
      const x = (i * 137.5) % 100;
      const y = (i * 97.3) % 100;
      const drift = Math.sin(f * 0.003 + i * 1.2) * 3;
      return <div key={i} style={{position:"absolute",left:`${x}%`,
        top:`${y + drift}%`,width:3+(i%3)*2,height:3+(i%3)*2,
        background:"#fff",opacity:0.04+(i%3)*0.01,borderRadius:1,
        willChange:"top",pointerEvents:"none"}}/>;
    })}
  </>;
};

// ─── Mascot ───────────────────────────────────────────────────────────────────
const Mascot: React.FC = () => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const rot = (f / fps) * 0.15 * 360;
  const op = interpolate(spring({frame:f,fps,config:SPRINGS.snappy}),[0,1],[0,1],CLAMP);
  return (
    <div style={{position:"absolute",top:72,right:60,width:64,height:64,
      display:"flex",alignItems:"center",justifyContent:"center",
      background:C.amber,borderRadius:"50%",opacity:op,
      transform:`rotate(${rot}deg)`,willChange:"transform,opacity",
      zIndex:100,fontSize:32,fontFamily:FONT_STACK}}>✳</div>
  );
};

// ─── Pill ─────────────────────────────────────────────────────────────────────
const Pill: React.FC<{text:string; bg:string; fg:string; delay?:number}> =
  ({text, bg, fg, delay=0}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: f - delay, fps, config: SPRINGS.snappy});
  const op = interpolate(p,[0,1],[0,1],CLAMP);
  const sc = interpolate(p,[0,1],[0.8,1],CLAMP);
  return (
    <div style={{display:"inline-flex",alignItems:"center",padding:"10px 20px",
      background:bg,borderRadius:100,opacity:op,transform:`scale(${sc})`,
      willChange:"transform,opacity"}}>
      <span style={{fontSize:15,fontWeight:700,color:fg,fontFamily:FONT_STACK,
        letterSpacing:"0.04em",textTransform:"uppercase"}}>{text}</span>
    </div>
  );
};

// ─── SlideUp ─────────────────────────────────────────────────────────────────
const SlideUp: React.FC<{text:string; color:string; size:number; weight:number; delay:number}> =
  ({text, color, size, weight, delay}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: f - delay, fps, config: SPRINGS.snappy});
  const y = interpolate(p,[0,1],[30,0],CLAMP);
  const op = interpolate(p,[0,1],[0,1],CLAMP);
  return (
    <div style={{overflow:"hidden"}}>
      <div style={{transform:`translateY(${y}px)`,opacity:op,willChange:"transform,opacity",
        fontSize:size,fontWeight:weight,color,fontFamily:FONT_STACK,lineHeight:1.1}}>
        {text}
      </div>
    </div>
  );
};

// ─── WordReveal ───────────────────────────────────────────────────────────────
const WordReveal: React.FC<{text:string; color:string; size:number; weight:number;
  delay:number; stagger?:number}> = ({text, color, size, weight, delay, stagger=5}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const words = text.split(" ");
  return (
    <div style={{display:"flex",flexWrap:"wrap",gap:"0.25em",
      fontFamily:FONT_STACK,fontSize:size,fontWeight:weight,color,lineHeight:1.3}}>
      {words.map((w, i) => {
        const p = spring({frame: f - delay - i * stagger, fps, config: SPRINGS.smooth});
        return (
          <span key={i} style={{opacity:interpolate(p,[0,1],[0,1],CLAMP),
            transform:`translateY(${interpolate(p,[0,1],[12,0],CLAMP)}px)`,
            willChange:"transform,opacity",display:"inline-block"}}>{w}</span>
        );
      })}
    </div>
  );
};

// ─── TimestampLog (SC-002) ────────────────────────────────────────────────────
const TimestampLog: React.FC<{entries: {time:string; label:string; delay:number}[]}> =
  ({entries}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  return (
    <div style={{display:"flex",flexDirection:"column",gap:28,
      background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",
      borderRadius:12,padding:"32px 36px",fontFamily:"SF Mono,monospace,system-ui"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,
        borderBottom:"1px solid rgba(255,255,255,0.06)",paddingBottom:16,marginBottom:4}}>
        {["#EF4444","#F59E0B","#22C55E"].map((c,i)=>(
          <div key={i} style={{width:10,height:10,borderRadius:"50%",background:c}}/>
        ))}
        <span style={{marginLeft:8,fontSize:11,color:"rgba(255,255,255,0.3)",
          letterSpacing:"0.08em",textTransform:"uppercase"}}>
          content-os · agents ativos
        </span>
      </div>
      {entries.map((entry, i) => {
        const p = spring({frame: f - entry.delay, fps, config: SPRINGS.snappy});
        const op = interpolate(p,[0,1],[0,1],CLAMP);
        const x = interpolate(p,[0,1],[-16,0],CLAMP);
        return (
          <div key={i} style={{display:"flex",alignItems:"center",gap:20,
            opacity:op,transform:`translateX(${x}px)`,willChange:"transform,opacity"}}>
            <span style={{fontSize:28,fontWeight:700,color:C.amber,minWidth:86,
              letterSpacing:"0.02em"}}>{entry.time}</span>
            <div style={{width:1,height:24,background:"rgba(255,255,255,0.12)"}}/>
            <span style={{fontSize:19,color:"rgba(255,255,255,0.7)",fontWeight:400}}>
              {entry.label}
            </span>
            <span style={{marginLeft:"auto",fontSize:13,color:C.teal,fontWeight:600}}>✓</span>
          </div>
        );
      })}
    </div>
  );
};

// ─── StatBlock ────────────────────────────────────────────────────────────────
const StatBlock: React.FC<{num:string; unit:string; label:string;
  numColor:string; delay:number}> = ({num, unit, label, numColor, delay}) => {
  const f = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: f - delay, fps, config: SPRINGS.snappy});
  const op = interpolate(p,[0,1],[0,1],CLAMP);
  const sc = interpolate(p,[0,1],[0.7,1],CLAMP);
  return (
    <div style={{opacity:op,transform:`scale(${sc})`,willChange:"transform,opacity"}}>
      <div style={{display:"flex",alignItems:"baseline",gap:6,marginBottom:4}}>
        <span style={{fontSize:80,fontWeight:900,color:numColor,
          fontFamily:FONT_STACK,lineHeight:1}}>{num}</span>
        <span style={{fontSize:28,fontWeight:700,color:numColor,
          fontFamily:FONT_STACK}}>{unit}</span>
      </div>
      <span style={{fontSize:14,color:C.muted,fontFamily:FONT_STACK,
        letterSpacing:"0.06em",textTransform:"uppercase"}}>{label}</span>
    </div>
  );
};

// ─── Main composition ─────────────────────────────────────────────────────────
export const ContentOsAgentsV3: React.FC = () => {
  const f = useCurrentFrame();

  return (
    <AbsoluteFill style={{background:C.bg}}>

      {/* ══════════════════════════════════════════════════════════════════
          SC-001 — HOOK (75f = 2.5s)
          Copy: "Você acordou. Os agents não dormiram."
          BG: BgKineticLines — vermelho/urgência, interrompe a atenção
          ══════════════════════════════════════════════════════════════════ */}
      <Sequence from={SC.s1.from} durationInFrames={SC.s1.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/agents-v1/ag01.mp3")} startFrom={0}/>
          <BgKineticLines lineColor={C.pain} count={22} amplitude={16} speed={1.4} opacity={0.52}/>
          <FilmGrain opacity={0.14}/>
          <Particles/>
          <Mascot/>
          <div style={{position:"absolute",left:60,right:60,top:HERO_TOP}}>
            <SlideUp text="Você acordou." color={C.white} size={72} weight={900} delay={0}/>
          </div>
          <div style={{position:"absolute",left:60,right:60,top:HERO_TOP + 100}}>
            <SlideUp text="Os agents" color={C.amber} size={72} weight={900} delay={22}/>
          </div>
          <div style={{position:"absolute",left:60,right:60,top:HERO_TOP + 200}}>
            <SlideUp text="não dormiram." color={C.white} size={48} weight={400} delay={30}/>
          </div>
          <CaptionBar words={[
            {text:"Você",     frame:2},
            {text:"acordou.", frame:9},
            {text:"Os",       frame:20},
            {text:"agents",   frame:26},
            {text:"não",      frame:34},
            {text:"dormiram.",frame:42},
          ]}/>
        </AbsoluteFill>
      </Sequence>

      {/* ══════════════════════════════════════════════════════════════════
          SC-002 — TIMELINE PROOF (143f = 4.8s)
          Copy: "Às 02h copy pronto. 03h vídeo. 07h publicado."
          BG: BgCircuitTrace — developer/pipeline register
          ══════════════════════════════════════════════════════════════════ */}
      <Sequence from={SC.s2.from} durationInFrames={SC.s2.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/agents-v1/ag02.mp3")} startFrom={0}/>
          <BgCircuitTrace traceColor={C.teal} speed={0.8} maxOpacity={0.45}/>
          <FilmGrain opacity={0.14}/>
          <Particles/>
          <Mascot/>
          <div style={{position:"absolute",top:PILL_TOP,left:60}}>
            <Pill text="ENQUANTO VOCÊ DORMIA" bg="rgba(20,184,166,0.15)"
              fg={C.teal} delay={0}/>
          </div>
          <div style={{position:"absolute",left:44,right:44,top:420}}>
            <TimestampLog entries={[
              {time:"02:00", label:"copy gerado pelos agents",      delay:8},
              {time:"03:00", label:"vídeo renderizado e legendado", delay:55},
              {time:"07:00", label:"agendado · publicado",          delay:100},
            ]}/>
          </div>
          <CaptionBar words={[
            {text:"Às",        frame:2},
            {text:"duas",      frame:8},
            {text:"da",        frame:14},
            {text:"manhã,",    frame:20},
            {text:"copy",      frame:30},
            {text:"pronto.",   frame:38},
            {text:"Às",        frame:55},
            {text:"três,",     frame:61},
            {text:"vídeo.",    frame:70},
            {text:"Às",        frame:85},
            {text:"sete,",     frame:91},
            {text:"publicado.",frame:102},
          ]}/>
        </AbsoluteFill>
      </Sequence>

      {/* ══════════════════════════════════════════════════════════════════
          SC-003 — PRODUCT REVEAL (91f = 3.0s)
          Copy: "Content OS. Quinze agents. Zero hora extra."
          BG: MeshBg teal — solução/alívio
          ══════════════════════════════════════════════════════════════════ */}
      <Sequence from={SC.s3.from} durationInFrames={SC.s3.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/agents-v1/ag03.mp3")} startFrom={0}/>
          <MeshBg color={C.teal} opacity={0.24} speed={0.5}/>
          <FilmGrain opacity={0.14}/>
          <Particles/>
          <Mascot/>
          <div style={{position:"absolute",top:PILL_TOP,left:60}}>
            <Pill text="01 / O SISTEMA" bg={C.teal} fg="#080808" delay={0}/>
          </div>
          <div style={{position:"absolute",left:60,right:60,top:260}}>
            <SlideUp text="Content OS." color={C.teal} size={80} weight={900} delay={4}/>
          </div>
          <div style={{position:"absolute",left:60,top:420,display:"flex",gap:40,
            alignItems:"center"}}>
            <div>
              <SlideUp text="15" color={C.amber} size={64} weight={900} delay={25}/>
              <WordReveal text="agents ativos" color={C.muted} size={16} weight={500}
                delay={32} stagger={3}/>
            </div>
            <div style={{width:1,height:60,background:"rgba(255,255,255,0.12)"}}/>
            <div>
              <SlideUp text="0h" color={C.teal} size={64} weight={900} delay={42}/>
              <WordReveal text="hora extra" color={C.muted} size={16} weight={500}
                delay={49} stagger={3}/>
            </div>
          </div>
          <div style={{position:"absolute",left:60,right:60,top:580}}>
            <WordReveal text="Um sistema que não precisa de você." color={C.white}
              size={26} weight={400} delay={55} stagger={4}/>
          </div>
          <CaptionBar words={[
            {text:"Content",  frame:2},
            {text:"OS.",      frame:11},
            {text:"Quinze",   frame:25},
            {text:"agents.",  frame:35},
            {text:"Zero",     frame:53},
            {text:"hora",     frame:63},
            {text:"extra.",   frame:72},
          ]}/>
        </AbsoluteFill>
      </Sequence>

      {/* ══════════════════════════════════════════════════════════════════
          SC-004 — MECHANISM (79f = 2.6s)
          Copy: "Um aprova a ideia. Eles fazem o resto."
          BG: MeshBg amber — mecanismo, contraste você/sistema
          ══════════════════════════════════════════════════════════════════ */}
      <Sequence from={SC.s4.from} durationInFrames={SC.s4.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/agents-v1/ag04.mp3")} startFrom={0}/>
          <MeshBg color={C.amber} opacity={0.18} speed={0.4}/>
          <FilmGrain opacity={0.14}/>
          <Particles/>
          <Mascot/>
          <div style={{position:"absolute",top:PILL_TOP,left:60}}>
            <Pill text="02 / COMO FUNCIONA" bg={C.amber} fg="#080808" delay={0}/>
          </div>
          <div style={{position:"absolute",left:60,top:300}}>
            <WordReveal text="VOCÊ" color="rgba(255,255,255,0.25)"
              size={48} weight={900} delay={4} stagger={0}/>
            <WordReveal text="1 aprovação" color="rgba(255,255,255,0.4)"
              size={20} weight={400} delay={8} stagger={3}/>
          </div>
          <div style={{position:"absolute",left:60,top:430,
            width:interpolate(Math.max(0,f - SC.s4.from - 10),[0,30],[0,580],CLAMP),
            height:1,background:"rgba(255,255,255,0.1)",willChange:"width"}}/>
          <div style={{position:"absolute",left:60,right:60,top:470}}>
            <SlideUp text="Eles fazem o resto." color={C.white} size={52}
              weight={800} delay={20}/>
            <div style={{marginTop:12}}>
              <WordReveal text="copy · vídeo · legenda · agendamento · publicação"
                color={C.muted} size={16} weight={400} delay={35} stagger={2}/>
            </div>
          </div>
          <CaptionBar words={[
            {text:"Um",     frame:2},
            {text:"aprova", frame:9},
            {text:"a",      frame:17},
            {text:"ideia.", frame:22},
            {text:"Eles",   frame:34},
            {text:"fazem",  frame:42},
            {text:"o",      frame:50},
            {text:"resto.", frame:56},
          ]}/>
        </AbsoluteFill>
      </Sequence>

      {/* ══════════════════════════════════════════════════════════════════
          SC-005 — NUMBERS (158f = 5.3s)
          Copy: "47 reels. Uma aprovação/semana. +312%."
          BG: BgDataFlow — AI/data/proof register
          ══════════════════════════════════════════════════════════════════ */}
      <Sequence from={SC.s5.from} durationInFrames={SC.s5.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/agents-v1/ag05.mp3")} startFrom={0}/>
          <BgDataFlow nodeColor={C.teal} intensity={1.2} speed={1}/>
          <FilmGrain opacity={0.14}/>
          <Particles/>
          <Mascot/>
          <div style={{position:"absolute",top:PILL_TOP,left:60}}>
            <Pill text="03 / OS NÚMEROS" bg={C.teal} fg="#080808" delay={0}/>
          </div>
          <div style={{position:"absolute",left:60,top:200}}>
            <StatBlock num="47" unit="reels" label="publicados automaticamente"
              numColor={C.white} delay={8}/>
          </div>
          <div style={{position:"absolute",left:60,top:430,
            width:1,height:interpolate(Math.max(0,f - SC.s5.from - 35),[0,20],[0,90],CLAMP),
            background:"rgba(255,255,255,0.12)",willChange:"height"}}/>
          <div style={{position:"absolute",left:80,top:430}}>
            <StatBlock num="1" unit="por semana" label="única aprovação sua"
              numColor={C.amber} delay={55}/>
          </div>
          <div style={{position:"absolute",left:60,right:60,top:660,
            height:1,background:"rgba(255,255,255,0.08)"}}/>
          <div style={{position:"absolute",left:60,right:60,top:700}}>
            <StatBlock num="+312%" unit="" label="alcance orgânico"
              numColor={C.teal} delay={105}/>
          </div>
          <CaptionBar words={[
            {text:"Quarenta",   frame:2},
            {text:"e",          frame:11},
            {text:"sete",       frame:16},
            {text:"reels.",     frame:24},
            {text:"Uma",        frame:44},
            {text:"aprovação",  frame:53},
            {text:"por",        frame:65},
            {text:"semana.",    frame:72},
            {text:"Mais",       frame:92},
            {text:"trezentos",  frame:100},
            {text:"e",          frame:113},
            {text:"doze",       frame:118},
            {text:"por",        frame:126},
            {text:"cento.",     frame:134},
          ]}/>
        </AbsoluteFill>
      </Sequence>

      {/* ══════════════════════════════════════════════════════════════════
          SC-006 — EPIPHANY (77f = 2.6s)
          Copy: "Um sistema que trabalha enquanto você dorme."
          BG: BgNoiseSolid — breath shot intencional, puro impacto
          ══════════════════════════════════════════════════════════════════ */}
      <Sequence from={SC.s6.from} durationInFrames={SC.s6.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/agents-v1/ag06.mp3")} startFrom={0}/>
          <BgNoiseSolid tint={C.teal}/>
          <FilmGrain opacity={0.12}/>
          <Mascot/>
          <div style={{position:"absolute",left:60,right:60,top:280}}>
            <WordReveal text="Um sistema que" color="rgba(255,255,255,0.5)"
              size={38} weight={400} delay={4} stagger={4}/>
          </div>
          <div style={{position:"absolute",left:60,right:60,top:360}}>
            <SlideUp text="trabalha enquanto" color={C.white} size={52} weight={800} delay={14}/>
          </div>
          <div style={{position:"absolute",left:60,right:60,top:440}}>
            <SlideUp text="você dorme." color={C.teal} size={52} weight={800} delay={24}/>
          </div>
          <CaptionBar words={[
            {text:"Um",       frame:2},
            {text:"sistema",  frame:9},
            {text:"que",      frame:17},
            {text:"trabalha", frame:24},
            {text:"enquanto", frame:36},
            {text:"você",     frame:50},
            {text:"dorme.",   frame:60},
          ]}/>
        </AbsoluteFill>
      </Sequence>

      {/* ══════════════════════════════════════════════════════════════════
          SC-007 — CTA AGENTE (75f = 2.5s)
          Copy: "Quer construir o seu? Comenta AGENTE."
          BG: BgGradientConic — premium/cinematic, máximo apelo visual
          ══════════════════════════════════════════════════════════════════ */}
      <Sequence from={SC.s7.from} durationInFrames={SC.s7.dur}>
        <AbsoluteFill>
          <Audio src={staticFile("audio/agents-v1/ag07.mp3")} startFrom={0}/>
          <BgGradientConic
            color1={C.amber}
            color2={C.teal}
            rotSpeed={0.05}
            opacity={0.35}
          />
          <FilmGrain opacity={0.12}/>

          {/* Profile photo */}
          <div style={{position:"absolute",top:120,left:0,right:0,
            display:"flex",flexDirection:"column",alignItems:"center",gap:16}}>
            {[3,2,1].map(ring => {
              const localF = Math.max(0, f - SC.s7.from);
              const ringP = spring({frame: localF - ring * 6, fps: 30, config: SPRINGS.smooth});
              const ringOp = interpolate(ringP,[0,1],[0, 0.4 - ring * 0.1],CLAMP);
              const size = 144 + ring * 52;
              return (
                <div key={ring} style={{
                  position:"absolute",
                  top: (144 - size) / 2, left: (144 - size) / 2,
                  width: size, height: size, borderRadius:"50%",
                  border:"1px solid rgba(245,158,11,0.5)",
                  opacity:ringOp,willChange:"opacity"}}/>
              );
            })}
            {(() => {
              const p = spring({frame: Math.max(0, f - SC.s7.from), fps: 30,
                config: SPRINGS.snappy});
              const sc = interpolate(p,[0,1],[0.8,1],CLAMP);
              const op = interpolate(p,[0,1],[0,1],CLAMP);
              return (
                <div style={{width:144,height:144,borderRadius:"50%",overflow:"hidden",
                  border:`2px solid ${C.amber}`,transform:`scale(${sc})`,opacity:op,
                  willChange:"transform,opacity",flexShrink:0}}>
                  <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
                    style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                </div>
              );
            })()}
          </div>

          {/* CTA text */}
          <div style={{position:"absolute",left:60,right:60,top:430,textAlign:"center"}}>
            <WordReveal text="Quer construir o seu?" color={C.white}
              size={36} weight={600} delay={10} stagger={3}/>
          </div>
          <div style={{position:"absolute",left:0,right:0,top:530,
            display:"flex",justifyContent:"center"}}>
            {(() => {
              const p = spring({frame: Math.max(0, f - SC.s7.from - 18), fps: 30,
                config: SPRINGS.snappy});
              const sc = interpolate(p,[0,1],[0.85,1],CLAMP);
              const op = interpolate(p,[0,1],[0,1],CLAMP);
              return (
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4,
                  opacity:op,transform:`scale(${sc})`,willChange:"transform,opacity"}}>
                  <span style={{fontSize:18,color:C.white,fontFamily:FONT_STACK,
                    fontWeight:400,letterSpacing:"0.02em"}}>Comenta</span>
                  <span style={{fontSize:72,fontWeight:900,color:C.amber,
                    fontFamily:FONT_STACK,lineHeight:1,letterSpacing:"-0.02em"}}>
                    AGENTE
                  </span>
                </div>
              );
            })()}
          </div>
          {/* Handle */}
          <div style={{position:"absolute",bottom:60,left:0,right:0,
            display:"flex",justifyContent:"center"}}>
            {(() => {
              const p = spring({frame: Math.max(0, f - SC.s7.from - 30), fps: 30,
                config: SPRINGS.smooth});
              return (
                <span style={{fontSize:14,color:"rgba(255,255,255,0.35)",
                  fontFamily:FONT_STACK,letterSpacing:"0.08em",
                  opacity:interpolate(p,[0,1],[0,1],CLAMP)}}>
                  @guilhermeribeiro.ai
                </span>
              );
            })()}
          </div>
          {/* Caption — above handle, below "AGENTE" */}
          <CaptionBar bottom={170} words={[
            {text:"Quer",       frame:2},
            {text:"construir",  frame:10},
            {text:"o",          frame:22},
            {text:"seu?",       frame:27},
            {text:"Comenta",    frame:40},
            {text:"AGENTE.",    frame:54},
          ]}/>
        </AbsoluteFill>
      </Sequence>

      {/* ─── Mascot persistent ─── */}
      {f < SC.s7.from && <Mascot/>}

    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
